import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CURRENCY } from "../../constants/Variables";

function RestockProductCard({ productDetail, currentLanguage }) {
  const { t } = useTranslation();
  const name = productDetail?.name?.[currentLanguage] || "-";
  const description = productDetail?.description?.[currentLanguage] || "-";
  const selectedVariant = productDetail?.variants?.[0];

  const selectedVariantAttributes = selectedVariant?.attributes
    ? Object.values(selectedVariant.attributes)
        .map(
          (attribute) =>
            attribute?.label?.[currentLanguage] || attribute?.value,
        )
        .filter(Boolean)
        .join(" / ")
    : "";

  const selectedVariantLabel =
    selectedVariantAttributes || selectedVariant?.sku || "-";

  const isVariant = productDetail?.isVariant;
  const productPrice = isVariant
    ? productDetail?.variants?.[0]?.finalPrice
    : productDetail?.finalPrice || productDetail?.price || 0;
  const productDiscount = isVariant
    ? productDetail?.variants?.[0]?.discount
    : productDetail?.discount || 0;
  const productProfitMargin = isVariant
    ? productDetail?.variants?.[0]?.profitMargin
    : productDetail?.profitMargin || 0;
  const productFinalPrice = isVariant
    ? productDetail?.variants?.[0]?.finalPrice
    : productDetail?.finalPrice || productDetail?.price || 0;
  const productSku = isVariant
    ? productDetail?.variants?.[0]?.sku
    : productDetail?.sku || "-";
  const productQuantity = isVariant
    ? productDetail?.variants?.[0]?.quantity
    : (productDetail?.quantity ?? 0);
  const mainImage = isVariant
    ? productDetail?.variants?.[0]?.images?.[0]
    : productDetail?.coverImage || productDetail?.images?.[0] || "";

  return (
    <Card
      sx={{
        mt: 2,
        border: "1px solid #E2E8F0",
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(180deg, #FFFFFF 0%, #FCFDFE 100%)",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        gap={0}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 210 },
            minWidth: { xs: "100%", md: 210 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 165 },
              height: { xs: 170, md: 165 },
              border: "1px solid #E2E8F0",
              borderRadius: 2,
              backgroundColor: "#F8FAFC",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {mainImage ? (
              <CardMedia
                component="img"
                image={mainImage}
                alt={name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  p: 1,
                }}
              />
            ) : (
              <Stack
                justifyContent="center"
                alignItems="center"
                sx={{ height: "100%", p: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("common.noImageAvailable")}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        <CardContent sx={{ flex: 1, p: { xs: 2, md: 2.5 } }}>
          <Stack gap={2}>
            <Stack gap={1.25}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                {name}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  label={
                    productDetail?.status
                      ? t("common.active")
                      : t("common.inactive")
                  }
                  size="small"
                  color={productDetail?.status ? "success" : "default"}
                  variant={productDetail?.status ? "filled" : "outlined"}
                  sx={{ borderRadius: 1.5, fontWeight: 500 }}
                />
                <Chip
                  label={
                    productDetail?.outOfStock
                      ? t("common.outOfStock")
                      : t("common.inStock")
                  }
                  size="small"
                  color={productDetail?.outOfStock ? "error" : "primary"}
                  variant={productDetail?.outOfStock ? "filled" : "outlined"}
                  sx={{ borderRadius: 1.5, fontWeight: 500 }}
                />
                <Chip
                  label={
                    productDetail?.isAllowedOrder
                      ? t("common.orderAllowed")
                      : t("common.orderBlocked")
                  }
                  size="small"
                  color={productDetail?.isAllowedOrder ? "success" : "warning"}
                  variant="outlined"
                  sx={{ borderRadius: 1.5, fontWeight: 500 }}
                />
              </Stack>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>

            <Divider sx={{ borderColor: "#E2E8F0" }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={`SKU: ${productSku}`}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "#F8FAFC",
                    borderColor: "#CBD5E1",
                    color: "#334155",
                  }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Qty: ${productQuantity}`}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "#F8FAFC",
                    borderColor: "#CBD5E1",
                    color: "#334155",
                  }}
                />
              </Stack>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {CURRENCY} {productPrice}
              </Typography>
            </Stack>

            {productDetail?.isVariant && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("common.selectedVariant")}:
                </Typography>
                <Chip
                  label={selectedVariantLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Stack>
    </Card>
  );
}

export default RestockProductCard;
